import React, { useCallback, useMemo, useRef } from "react";
import { InfiniteQueryResult } from "react-query";
import { AgGridReactProps } from "@ag-grid-community/react";
import { GridApi, ICellRendererParams, RowNode } from "@ag-grid-community/core";

import { DataGrid } from "fe-shared/src/components";
import {
  DataGridFilters,
  DataGridOptions,
} from "fe-shared/src/components/DataGrid/modules/types";

import {
  PickupRequestListItem,
  PickupRequestsListResponse,
  PickupRequestStatus,
} from "../../types";

import styles from "./RequestsDataGrid.module.scss";

export interface RequestsDataGridProps extends AgGridReactProps {
  requests: InfiniteQueryResult<PickupRequestsListResponse>;
  frameworkComponents: Record<string, unknown>;
  options: DataGridOptions;
  filters: DataGridFilters;
  setOptions: (options: DataGridOptions) => void;
  setFilters: (filters: DataGridFilters) => void;
  lastOpenedRequestId: string | null;
  clearLastOpenedRequestId: () => void;
  title: React.ReactNode;
  headerActionsSlot?: React.ReactNode;
  keyboardInteractiveColIds?: string[];
  onGridApiReady?: (api: GridApi) => void;
}

const gridOptions = {
  rowClassRules: {
    [styles.inactive]: (params: ICellRendererParams) =>
      params.data?.statusCode === PickupRequestStatus.InReview,
  },
};

const RequestsDataGrid = (props: RequestsDataGridProps) => {
  const {
    lastOpenedRequestId,
    clearLastOpenedRequestId,
    requests,
    filters,
    title,
    headerActionsSlot,
    keyboardInteractiveColIds = [],
    onGridApiReady,
    ...otherProps
  } = props;

  const gridApiRef = useRef<GridApi | null>(null);

  const assignGridApi = (api: GridApi) => {
    gridApiRef.current = api;
    onGridApiReady?.(api);
  };

  const onFirstDataRendered = useCallback(() => {
    if (lastOpenedRequestId) {
      gridApiRef.current!.ensureNodeVisible(
        (node: RowNode) => node.id === lastOpenedRequestId,
        "middle"
      );

      clearLastOpenedRequestId();
    }
  }, [lastOpenedRequestId, clearLastOpenedRequestId]);

  const rowCount = requests.data?.[0].total;

  const requestList = useMemo(
    () =>
      requests.data?.reduce(
        (acc: PickupRequestListItem[], page: PickupRequestsListResponse) =>
          acc.concat(page.pickupRequests),
        []
      ),
    [requests.data]
  );

  return (
    <main className={styles.container}>
      <div className={styles.headerBlock}>
        <h1 className={styles.header}>
          {title}
          {!!rowCount && <span className={styles.totalCount}>{rowCount}</span>}
        </h1>
        <div className={styles.buttons}>{headerActionsSlot}</div>
      </div>
      <DataGrid
        idField="requestNumber"
        filters={filters}
        suppressCellSelection
        assignGridApi={assignGridApi}
        onFirstDataRendered={onFirstDataRendered}
        isLoading={requests.isLoading}
        rowData={requestList}
        fetchMore={requests.fetchMore}
        canFetchMore={requests.canFetchMore}
        isFetchingMore={requests.isFetchingMore}
        className={styles.requestsDataGrid}
        headerHeight={48}
        suppressKeyboardEvent={(params) =>
          keyboardInteractiveColIds.includes(params.column.getColId())}
        gridOptions={gridOptions}
        {...otherProps}
      />
    </main>
  );
};

export default RequestsDataGrid;
